import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";

import { supabase } from "@/integrations/supabase/client";
import { money, planPrice, SITE_PLAN, type PlanInterval } from "@/lib/sitePlan";

import { buy, canBuy, isNativeApp, isSilentAccount, restore, storeOffer, type StoreOffer } from "./appStore";
import { ICON } from "./icons";
import { barnCountLabel, fetchPaywallProof, type PaywallProof } from "@/lib/memberProof";

/**
 * Screen B — the app's subscribe screen (owner, 2026-10-22: "I like B", then "b is good").
 * Mock: https://claude.ai/artifact/ECYJop6pSBw5XdxYQ7ZjJt · plan: https://claude.ai/artifact/FuFygyGs57jrS1cexGSpaT
 * ONE PLAN since 2026-10-25 (src/lib/sitePlan.ts): one card, monthly or yearly, where the mock
 * drew Basic beside Premium.
 *
 * THE HERO IS THE 200+ BARNS (owner, 2026-10-25: "We really have to highlight they are getting
 * prices for over 200 barns live. No one else does this."): the count in big gold type, the one
 * claim in words, and a strip of the barns selling right now — real figures from the ticker,
 * never a canned list (src/lib/memberProof.ts). Under the button, "What a Member gets" lists
 * everything by category for the person who scrolls. Research behind the shape (2026-10-25):
 * short outcome bullets on the card, the full list below the fold, the price after the trial
 * under the button (Apple 3.1.2).
 *
 * Shown on any locked screen in the app, once on first open (with ✕), and from the You tab.
 * No sign-up before paying: "Start free month" → Apple's Face ID sheet → done. Website members
 * use "Already a member? Sign in".
 *
 * ⚠️ WHAT APPLE REQUIRES ON THIS SCREEN (3.1.2): the price after the free month in plain words,
 * Restore, Terms and Privacy, and that it is cancelled in Settings. And never "free" to someone
 * Apple will charge at once — the button and the fine print follow storeOffer().freeMonth.
 *
 * ⚠️ WHITE ON NAVY, ALWAYS — never a grey line (owner, 2026-10-22: "Stop doing grey text on dark
 * background"). The screen is navy in light and dark mode alike.
 */

/** One glyph per catalogue row (src/rbapp/icons.tsx). */
const CAT_ICON: Record<string, ReactNode> = {
  "Live feeder market": ICON.ticker,
  "The Cattle Market Central Indexes": ICON.stairs,
  "Packer, beef & supply": ICON.box,
  "Reports, Clara & alerts": ICON.sparkles,
};

interface Props {
  /** Shown as a sheet with ✕ (first open) or as its own screen. Absent on a locked screen. */
  onClose?: () => void;
  /** It is its own route (/m/subscribe): after a purchase it replaces itself, never goes back. */
  routed?: boolean;
}

export default function Paywall({ onClose, routed = false }: Props) {
  const navigate = useNavigate();
  const [interval, setInterval] = useState<PlanInterval>("monthly");
  const [offer, setOffer] = useState<StoreOffer | null>(null);
  const [proof, setProof] = useState<PaywallProof | null>(null);
  const [busy, setBusy] = useState<"buy" | "restore" | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const native = isNativeApp();
  const ready = canBuy();
  // In a browser (raisedbeef.com/m) there is no Apple sheet: a signed-in person goes straight
  // to the website's checkout for the plan they picked; a stranger gets the website's own
  // free month (no card) at sign-up. Owner, 2026-10-22: "don't they have to pick a plan?"
  const [webSignedIn, setWebSignedIn] = useState(false);
  useEffect(() => {
    if (native) return;
    let live = true;
    void supabase.auth.getSession().then(({ data }) => { if (live) setWebSignedIn(!!data.session); });
    return () => { live = false; };
  }, [native]);

  useEffect(() => {
    let live = true;
    if (ready) void storeOffer().then((o) => { if (live) setOffer(o); });
    return () => { live = false; };
  }, [ready]);

  useEffect(() => {
    let live = true;
    void fetchPaywallProof().then((p) => { if (live) setProof(p); });
    return () => { live = false; };
  }, []);

  const priceOf = (i: PlanInterval) => offer?.prices[i] ?? money(planPrice(i));
  const per = interval === "monthly" ? "a month" : "a year";
  const free = !!offer?.freeMonth;
  const after = `${priceOf(interval)} ${per}`;
  // The yearly badge is a saving, never "free": a second "free" claim beside
  // "First month free" reads as two trials, and is false for an Apple ID that
  // has used its intro offer (App Review 3.1.2, audit 2026-10-31).
  const yearlySave = Math.round((1 - planPrice("annual") / (12 * planPrice("monthly"))) * 100);
  // Apple asks that the purchase screen say the plan renews on its own and how
  // to stop it (3.1.2(c)); the store description says it, now the screen does.
  const renews = "Renews automatically until cancelled in Settings › Subscriptions, at least 24 hours before the period ends.";
  // Before the store answers, say nothing about free: the fine print must be true.
  const fine = !offer
    ? `${after}. ${renews}`
    : free ? `Free for 1 month, then ${after}. ${renews}` : `${after}. ${renews}`;

  const start = async () => {
    setMsg(null);
    if (!native) {
      // The web app at raisedbeef.com/m: sign-up and checkout stay in the frame.
      // Until 2026-10-31 this was a hard jump to the website's /account, which
      // dropped the person on the website's account page — "takes me to
      // RaisedBeef.com and won't let me do it from the app". Now: the website's
      // own join form rendered inside the frame, back to You when done; and
      // Stripe returns to You, not to /account.
      if (!webSignedIn) { navigate("/m/t/join?next=/m/you"); return; }
      setBusy("buy");
      const { data, error } = await supabase.functions.invoke("rb-site-checkout", { body: { interval, origin: window.location.origin, return_to: "/m/you" } });
      const url = (data as { url?: string } | null)?.url;
      if (error || !url) { setBusy(null); setMsg("Couldn't open checkout. Try again in a minute."); return; }
      window.location.href = url;
      return;
    }
    setBusy("buy");
    const r = await buy(interval);
    setBusy(null);
    if (r.ok) {
      // Paid on a silent account → offer the one-tap save (Sign in with Apple), skippable. Its
      // "Not now" goes back, so from a locked tool it lands on that tool, now open.
      const silent = await isSilentAccount();
      if (routed) { navigate(silent ? "/m/save" : "/m", { replace: true }); return; }
      onClose?.();
      if (silent) navigate("/m/save");
      return;
    }
    if (!r.cancelled) setMsg(r.message);
  };

  const doRestore = async () => {
    setMsg(null); setBusy("restore");
    const r = await restore();
    setBusy(null);
    setMsg(r.message);
    if (r.found) onClose?.();
  };

  return (
    <div className="rba-pw" data-sheet={onClose ? "1" : undefined}>
      {onClose && <button type="button" className="rba-pw-x" aria-label="Close" onClick={onClose}>✕</button>}
      <img className="rba-pw-logo" src="/brand/cmc/logo-white.png" alt="Cattle Market Central" />
      <div className="rba-pw-by">
        <span>DRIVEN BY</span>
        <img src="/brand/rbapp/dvauction-white.png" alt="DVAuction" />
        <img src="/brand/rbapp/raisedbeef-white.png" alt="RaisedBeef" />
      </div>

      {/* The hero: the one thing nobody else has. */}
      <section className="rba-pw-hero" aria-label="Live sale barns">
        <div className="rba-pw-big">
          <span className="rba-pw-n">{barnCountLabel(proof?.barnCount ?? null)}</span>
          <span className="rba-pw-lbl">Sale barns,<br />live as they sell</span>
        </div>
        <p className="rba-pw-claim">{SITE_PLAN.claim}</p>
        {proof && proof.lots.length > 0 && (
          <div className="rba-pw-strip" aria-label="Selling now">
            {proof.lots.map((l) => (
              <div key={`${l.state}|${l.barn}`} className="rba-pw-lot" data-live={l.live ? "1" : undefined}>
                <small>{l.short}, {l.state}</small>
                <b>{l.priceLabel}</b>
                <span>{l.line}{l.live ? " · selling now" : ""}</span>
              </div>
            ))}
          </div>
        )}
        <p className="rba-pw-also">Plus all {SITE_PLAN.indexCount} Cattle Market Central Indexes, the averages, the maps, Clara and every tool. One plan.</p>
      </section>

      {/* "Free" in the headline only when the fine print says it too (Apple 3.1.2). */}
      <h2 className="rba-pw-h">{free ? <>First month <span>free</span></> : <>Everything, <span>one plan</span></>}</h2>

      <div className="rba-pw-seg" role="group" aria-label="Billing">
        <button type="button" aria-pressed={interval === "monthly"} onClick={() => setInterval("monthly")}>Monthly</button>
        <button type="button" aria-pressed={interval === "annual"} onClick={() => setInterval("annual")}>Yearly{yearlySave > 0 && <em>SAVE {yearlySave}%</em>}</button>
      </div>

      <div className="rba-pw-cards" data-one="1">
        <div className="rba-pw-card" aria-checked="true" role="radio">
          <span className="rba-pw-nm">{SITE_PLAN.short}</span>
          <span className="rba-pw-pr">{priceOf(interval)}<small>{interval === "monthly" ? "/mo" : "/yr"}</small></span>
          <ul>{SITE_PLAN.includesShort.map((x) => <li key={x}>{x}</li>)}</ul>
        </div>
      </div>

      <button type="button" className="rba-pw-go" onClick={start} disabled={busy !== null || (native && !ready)}>
        {busy === "buy" ? "Opening…" : !native ? (webSignedIn ? "Subscribe" : "Start free month") : !ready ? "Opening soon" : free ? "Start free month" : "Subscribe"}
      </button>
      <p className="rba-pw-fine">
        {native && !ready ? "Subscriptions open in the next update."
          : !native && !webSignedIn ? "Everything opens free for 30 days. No card. Then subscribe."
          : fine}
      </p>

      {msg && <p className="rba-pw-msg" role="status">{msg}</p>}

      {/* Under the fold: everything, for the person who scrolls because they want to know. */}
      <section className="rba-pw-all" aria-label="What a Member gets">
        <h3>What a {SITE_PLAN.short} gets</h3>
        <h4>Everything we publish</h4>
        <div className="rba-pw-nums">
          <div><b>{barnCountLabel(proof?.barnCount ?? null)}</b><span>Barns live</span></div>
          <div><b>{SITE_PLAN.indexCount}</b><span>Indexes</span></div>
          <div><b>{SITE_PLAN.toolCount}</b><span>Tools</span></div>
        </div>
        {SITE_PLAN.catalogue.map((c) => (
          <div key={c.title} className="rba-pw-cat">
            <i aria-hidden="true">{CAT_ICON[c.title] ?? ICON.sparkles}</i>
            {/* Sale Sheet is a website page with no screen in the app: the app does not sell it (2.3, audit 2026-10-31). */}
            <div><b>{c.title}<em>{c.count}</em></b><span>{c.items.replace(" · Sale Sheet", "")}</span></div>
          </div>
        ))}
      </section>

      <div className="rba-pw-links">
        {native && <button type="button" onClick={doRestore} disabled={busy !== null}>{busy === "restore" ? "Restoring…" : "Restore"}</button>}
        <Link to="/m/t/terms" onClick={onClose}>Terms</Link>
        <Link to="/m/t/privacy" onClick={onClose}>Privacy</Link>
      </div>
      <Link className="rba-pw-member" to="/m/account" onClick={onClose}>Already a member? <b>Sign in</b></Link>
    </div>
  );
}
